import { Chip } from "@shared/components/chip";
import { useAccess } from "@templates/course/hooks/useAccess";
import { toFarsiNumber } from "@utils/digits";
import { FC, useMemo } from "react";
import ProductCoverImage from "../sidebar/default/CoverImage";
import { COURSE_TYPE } from "@types";
import { useNonAccess } from "@templates/course/hooks/useNonAccess";
import ProductPageSidebarPublishedFooter from "../sidebar/default/footer/Published";
import RecordignProductSidebarFooter from "../sidebar/default/footer/Recording";
import classNames from "classnames";
import LayoutAction from "./layoutAction";
import Star from "./Star";
import Graduated from "@svg/new-design/graduated";

const ProductPageHeaderContent: FC = () => {
  const {
    courseData: {
      product: {
        title,
        instructors,
        rating,
        attribute_type,
        published_at,
        hasCertificate,
        slug,
        sold_count,
      },
      user: { is_favorite },
    },
    favoriteLoading,
    setFavoriteAction,
    copyCourseLink,
  } = useAccess();
  const { isPublished } = useNonAccess();

  const propTags = useMemo(() => {
    let result = [];
    // if (attribute_type === COURSE_TYPE.COURSE) {
    //   result.push("ویدیویی | زیرنویس");
    // } else
    if (attribute_type === COURSE_TYPE.AUDIOBOOK)
      result.push({ title: "پادکست", color: "bg-fds-inverse-secondary" });
    else if (attribute_type === COURSE_TYPE.BLIND)
      result.push({
        title: "نسخه ناشنوایان",
        color: "bg-fds-inverse-secondary",
      });
    else if (attribute_type === COURSE_TYPE.EXAM)
      result.push({ title: "آزمون", color: "bg-fds-inverse-secondary" });
    else if (attribute_type === COURSE_TYPE.TEXT)
      result.push({ title: "درس متنی", color: "bg-fds-inverse-secondary" });
    const timeDifferenceInMS = Date.now() - new Date(published_at).getTime();
    const twoWeeks = 2 * 7 * 24 * 60 * 60 * 1000;
    if (hasCertificate)
      result.push({ title: "گواهینامه", color: "bg-fds-inverse-secondary" });
    if (timeDifferenceInMS < twoWeeks && isPublished)
      result.push({ title: "تازه منتشرشده", color: "bg-fds-accent" });
    return result;
  }, [attribute_type, published_at]);

  const headerText = !isPublished
    ? "با پیش‌ ثبت‌نام در این آموزش، اولویت انتشار آن را بالا برده و پس از انتشار نهایی، تخفیف ویژه دریافت می‌کنید."
    : "";
  return (
    <>
      <div className="flex flex-col   w-full">
        <div className="block lg:hidden">
          <ProductCoverImage key={slug} />
        </div>
        <h1 className="headline-md mt-3 lg:mt-0 lg:display-sm header-text-max-w">
          {toFarsiNumber(title)}
        </h1>
        {isPublished ? (
          <div className="text-fds-brand flex items-center lg:justify-start justify-between mt-0.5  lg:mt-2 ">
            {Boolean(rating.total) ? (
              <div className="flex items-center lg:gap-2 lg:py-2 gap-1">
                <Star
                  fillWidth={1}
                  size={16}
                  className="grow shrink-0 min-w-4"
                />
                <p className="title-md-medium lg:title-xlg text-fds-secondary ">
                  {toFarsiNumber(rating.rate)}
                </p>
                <p className="button-sm md:title-md-medium text-fds-tertiary mr-1.5 lg:mr-0 ">
                  (بر اساس {toFarsiNumber(rating.total.toLocaleString())} امتیاز
                  )
                </p>
              </div>
            ) : (
              <>
                {Boolean(propTags.length) && (
                  <div className="flex gap-2  flex-wrap">
                    {(propTags || [])?.map((tag, i) => (
                      <Chip
                        key={i}
                        label={tag.title}
                        className={classNames(tag.color, "text-fds-on-inverse")}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            {Boolean(sold_count) && (
              <div className="hidden lg:flex">
                <div className="mx-4  w-px h-fds-18 border-l border-fds-default" />
                <div className=" gap-x-1.5 lg:flex hiddedn">
                  <Graduated className="h-6 w-6 text-fds-icon-tertiary" />
                  <p className="title-md-demibold text-fds-secondary">
                    {toFarsiNumber(sold_count.toLocaleString())} دانشجو
                  </p>
                </div>
              </div>
            )}
            <LayoutAction
              favoriteLoading={favoriteLoading}
              is_favorite={is_favorite}
              setFavoriteAction={setFavoriteAction}
              copyCourseLink={copyCourseLink}
              slug={slug}
            />
          </div>
        ) : null}

        {Boolean(sold_count) && (
          <div className=" gap-x-1.5 flex lg:hidden items-center">
            <Graduated className="h-4 w-4 text-fds-icon-tertiary" />
            <p className="title-sm text-fds-secondary">
              {toFarsiNumber(sold_count.toLocaleString())} دانشجو
            </p>
          </div>
        )}

        <p className=" hidden lg:block mt-2 body-xlg header-text-max-w">
          {toFarsiNumber(headerText)}
        </p>

        {Boolean(propTags.length) && Boolean(rating.total) && (
          <div className="flex items-center lg:mt-2 mt-3 justify-between gap-2">
            <div className="flex gap-2  flex-wrap">
              {(propTags || [])?.map((tag, i) => (
                <Chip
                  key={i}
                  label={tag.title}
                  className={classNames(tag.color, "text-fds-on-inverse")}
                />
              ))}
            </div>
          </div>
        )}

        <p className="text-fds-tertiary lg:mt-2 mt-3 lg:py-1.5   body-sm mb-3 lg:mb-0">
          مدرس:{" "}
          <span className="text-fds-secondary">
            {(instructors || []).map(
              (instructor, i) =>
                `${i > 0 ? "، " : ""}${instructor.first_name || ""} ${
                  instructor.last_name || ""
                }`
            )}
          </span>
        </p>
        <div className="w-full flex lg:hidden flex-col ">
          <div className="h-px w-full bg-divider   mb-3" />
          {isPublished ? (
            <ProductPageSidebarPublishedFooter dense />
          ) : (
            <RecordignProductSidebarFooter />
          )}
        </div>
      </div>
    </>
  );
};

export default ProductPageHeaderContent;
